import { useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Card, CardHeader } from '../../components/ui/Card';
import { Table, THead, Th, Td, Nil, TableFooter } from '../../components/ui/Table';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { channelLabel } from './shared';
import { knownPeople } from './known-people';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { formatDayMonthTime, formatTime } from '../../utils/dateFormat';
import { useIdentifiedPeople, useJourney, useJourneys } from '../../hooks/useJourneys';
import type { IdentifiedPersonRow, JourneyListRow, JourneyTimelineItem, JourneyVisit, ListPeriod } from '../../api/web-analytics';

/** Fillout forms by id, so a timeline reads "free trial form" rather than an id. */
const FORM_NAMES: Record<string, string> = {
  wx3coR34imus: 'free trial form',
  pvn93iNSpqus: 'demo form',
};

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtDate(iso: string): string {
  return formatDayMonthTime(iso);
}

function fmtTime(iso: string): string {
  return formatTime(iso);
}

function describe(item: JourneyTimelineItem): string {
  switch (item.kind) {
    case 'page': {
      const detail = [
        item.activeSeconds !== null ? fmtDuration(item.activeSeconds) : null,
        item.scrollPct !== null ? `read to ${item.scrollPct}%` : null,
      ].filter(Boolean);
      return `Viewed ${item.path}${detail.length > 0 ? `: ${detail.join(', ')}` : ''}`;
    }
    case 'click':
      return `Clicked “${item.label}”`;
    case 'form': {
      const form = (item.formId && FORM_NAMES[item.formId]) ?? 'a form';
      return `${item.step === 'viewed' ? 'Saw' : item.step === 'started' ? 'Started' : 'Submitted'} the ${form}`;
    }
    case 'conversion':
      return `Converted: ${item.formName ?? 'form submitted'}`;
  }
}

/** One person on the list: named by a form (full journey) or identified by RB2B (pages only). */
type PersonRow =
  | { kind: 'form'; key: string; lastActive: string; row: JourneyListRow }
  | { kind: 'rb2b'; key: string; lastActive: string; row: IdentifiedPersonRow };

/**
 * Marketing → Website: the people we know by name — from a form they filled in
 * (their full journey: visits, pages read, clicks, form steps) or from RB2B
 * (the pages they viewed). Click a row for the timeline.
 */
export function PeopleCard({ period, label }: { period: ListPeriod; label?: string }) {
  const journeys = useJourneys(period);
  const identified = useIdentifiedPeople(period);
  const [selected, setSelected] = useState<string | null>(null);

  // Memoised: a fresh array each render would reset the pager to page 1 on every click.
  const { rows, hiddenTests } = useMemo(() => {
    // Same rule as the Website tile: QA identities hidden and counted, RB2B duplicates of form people dropped.
    const { forms, rb2b, hiddenTests } = knownPeople(journeys.data?.journeys ?? [], identified.data?.people ?? []);
    const rows: PersonRow[] = [
      ...forms.map((row) => ({ kind: 'form' as const, key: `form:${row.email}`, lastActive: row.lastSeenAt, row })),
      ...rb2b.map((row) => ({ kind: 'rb2b' as const, key: `rb2b:${row.email}`, lastActive: row.lastSeenAt ?? '', row })),
    ].sort((a, b) => b.lastActive.localeCompare(a.lastActive));
    return { rows, hiddenTests };
  }, [journeys.data, identified.data]);

  // "Form" and "1 page" on nearly every row: say them once, and only mark the exceptions.
  const usualSource = dominant(rows, knownFrom);
  const usualPages = dominant(rows, pageCount);
  // Time on site is only measured for form journeys; when 90%+ of rows have none, the column is all dots.
  const showTime = rows.filter((r) => activeSeconds(r) > 0).length / Math.max(rows.length, 1) > 0.1;
  const pager = usePagination(rows, 15);
  const isLoading = journeys.isLoading || identified.isLoading;
  const failed = [journeys.isError && 'form journeys', identified.isError && 'RB2B people'].filter(Boolean);
  // Say only the sources that named someone: "or identified by RB2B" read wrong when RB2B named nobody.
  const formCount = rows.filter((r) => r.kind === 'form').length;
  const rb2bCount = rows.length - formCount;
  // Why fewer than the funnel's website-form leads (17 vs 40 over 90 days on 13 Oct): a form
  // only names a visitor here when our tracking saw the visit, i.e. they accepted cookies.
  const consentNote =
    formCount > 0 ? ' Form fillers appear only if they accepted cookies, so fewer than the funnel’s website-form leads.' : '';
  const description =
    formCount > 0 && rb2bCount > 0
      ? `${formCount} from a form they filled in, ${rb2bCount} identified by RB2B.${consentNote} Click one for their timeline.`
      : rb2bCount > 0
        ? 'Identified by RB2B. Click one for the pages they viewed.'
        : `From a form they filled in.${consentNote} Click one for their timeline.`;

  return (
    <Card>
      <CardHeader
        title="People known by name"
        description={label ? `${label} · ${description}` : description}
      />
      {failed.length > 0 && (
        <div className="pb-3">
          <ErrorState compact title={`Couldn’t load ${failed.join(' and ')}`} />
        </div>
      )}
      {isLoading ? (
        <LoadingState rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          title="Nobody identified in this period"
          description="Visitors are named when they submit a form with cookies accepted, or when RB2B recognises them."
        />
      ) : (
        <>
          <Table minWidth={720}>
            <THead>
              <tr>
                <Th>Person</Th>
                <Th>Company</Th>
                {usualSource === null && <Th>Known from</Th>}
                <Th align="right">Last active</Th>
                {usualPages === null && <Th align="right">Pages</Th>}
                {showTime && <Th align="right">Time on site</Th>}
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <PersonRowView
                  key={r.key}
                  person={r}
                  usualSource={usualSource}
                  usualPages={usualPages}
                  showTime={showTime}
                  open={selected === r.key}
                  onToggle={() => setSelected(selected === r.key ? null : r.key)}
                />
              ))}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
        </>
      )}
      {!isLoading && (hiddenTests > 0 || usualSource !== null || usualPages !== null || (!showTime && rows.length > 0)) && (
        <TableFooter>
          {[
            (usualSource !== null || usualPages !== null) &&
              `Unless noted: ${[
                usualSource !== null && `known from ${usualSource === 'RB2B' ? 'RB2B' : `a form (${usualSource.toLowerCase()})`}`,
                usualPages !== null && `${usualPages} ${usualPages === 1 ? 'page' : 'pages'} viewed`,
              ]
                .filter(Boolean)
                .join(', ')}`,
            !showTime && rows.length > 0 && 'Time on site hidden: not measured for almost anyone',
            !identified.isError && rb2bCount === 0 && rows.length > 0 && 'RB2B named nobody new in this period',
            hiddenTests > 0 && `${hiddenTests} test ${hiddenTests === 1 ? 'row' : 'rows'} hidden`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </TableFooter>
      )}
    </Card>
  );
}

function knownFrom(p: PersonRow): string {
  return p.kind === 'form' ? `Form${p.row.forms > 1 ? ` (${p.row.forms})` : ''}` : 'RB2B';
}

function activeSeconds(p: PersonRow): number {
  return p.kind === 'form' ? p.row.activeSeconds : 0;
}

function pageCount(p: PersonRow): number {
  return p.kind === 'form' ? p.row.pageviews : p.row.visits;
}

/** The value at least 90% of rows share, or null when the column varies enough to keep. */
function dominant<V>(rows: readonly PersonRow[], pick: (p: PersonRow) => V): V | null {
  if (rows.length < 2) return null;
  const counts = new Map<V, number>();
  for (const r of rows) counts.set(pick(r), (counts.get(pick(r)) ?? 0) + 1);
  for (const [v, n] of counts) if (n / rows.length >= 0.9) return v;
  return null;
}

function PersonRowView({
  person,
  usualSource,
  usualPages,
  showTime,
  open,
  onToggle,
}: {
  person: PersonRow;
  usualSource: string | null;
  usualPages: number | null;
  showTime: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const p = person;
  // Name first, email under it: from the form or the Attio person, else RB2B; the email when nobody gave a name.
  const fullName = p.row.fullName;
  const attioUrl = p.kind === 'form' ? p.row.attioUrl : null;
  const name = fullName ?? p.row.email;
  const pages = pageCount(p);
  const exceptions = [
    usualSource !== null && knownFrom(p) !== usualSource && `Known from ${knownFrom(p)}`,
    usualPages !== null && pages !== usualPages && `${pages} ${pages === 1 ? 'page' : 'pages'}`,
  ];
  const sub = [
    fullName ? p.row.email : null,
    ...(p.kind === 'rb2b' ? [p.row.title] : []),
    ...exceptions,
  ]
    .filter(Boolean)
    .join(' · ');
  const colSpan = 6 - (usualSource !== null ? 1 : 0) - (usualPages !== null ? 1 : 0) - (showTime ? 0 : 1);
  return (
    <>
      <tr
        onClick={onToggle}
        className={`border-b border-border-light cursor-pointer hover:bg-surface-secondary ${open ? 'bg-surface-secondary' : ''}`}
      >
        <Td className="max-w-[18rem]">
          <div className="flex items-start gap-1">
            <span className="break-words text-text-primary">{name}</span>
            {attioUrl && (
              <a
                href={attioUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                aria-label={`Open ${name} in Attio`}
                title="Open in Attio"
                className="mt-0.5 shrink-0 text-text-tertiary hover:text-accent"
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
          {sub && <div className="break-all text-body-xs text-text-tertiary">{sub}</div>}
        </Td>
        <Td className="break-words text-text-secondary">{p.row.companyName ?? <Nil />}</Td>
        {usualSource === null && <Td className="whitespace-nowrap text-text-secondary">{knownFrom(p)}</Td>}
        <Td align="right" className="whitespace-nowrap text-text-secondary">
          {p.lastActive ? fmtDate(p.lastActive) : <Nil />}
        </Td>
        {usualPages === null && (
          <Td align="right" className="tabular-nums text-text-secondary">
            {pages}
          </Td>
        )}
        {showTime && (
          <Td align="right" className="whitespace-nowrap text-text-secondary">
            {p.kind === 'form' && p.row.activeSeconds > 0 ? fmtDuration(p.row.activeSeconds) : <Nil />}
          </Td>
        )}
      </tr>
      {open && (
        <tr className="border-b border-border-light">
          <td colSpan={colSpan} className="py-3">
            {p.kind === 'form' ? <Timeline email={p.row.email} /> : <Rb2bPages person={p.row} />}
          </td>
        </tr>
      )}
    </>
  );
}

/** An RB2B-identified person's page views, newest first. */
function Rb2bPages({ person }: { person: IdentifiedPersonRow }) {
  if (person.pages.length === 0) return <p className="text-body-sm text-text-tertiary">No pages recorded.</p>;
  return (
    <div className="pl-2">
      {person.linkedinUrl && (
        <a href={person.linkedinUrl} target="_blank" rel="noreferrer" className="text-body-sm text-accent hover:underline">
          LinkedIn profile
        </a>
      )}
      <ul className="mt-1 space-y-0.5 border-l border-border pl-3">
        {person.pages.map((pg, i) => (
          <li key={`${pg.at}-${i}`} className="text-body-sm text-text-secondary">
            <span className="tabular-nums text-text-tertiary mr-2">{fmtDate(pg.at)}</span>
            Viewed {pg.path}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Timeline({ email }: { email: string }) {
  const { data, isLoading, isError } = useJourney(email);
  if (isLoading) return <LoadingState rows={2} />;
  if (isError || !data) return <ErrorState compact title="Couldn’t load this journey" />;
  if (data.visits.length === 0) return <EmptyState compact title="No tracked visits" />;

  return (
    <ol className="space-y-4 pl-2">
      {data.visits.map((visit, i) => (
        <VisitBlock key={visit.startedAt} visit={visit} index={i + 1} />
      ))}
    </ol>
  );
}

function VisitBlock({ visit, index }: { visit: JourneyVisit; index: number }) {
  return (
    <li>
      <p className="text-body-sm font-semibold text-text-primary">
        Visit {index} · {fmtDate(visit.startedAt)} ·{' '}
        <span className="font-normal text-text-secondary">
          {channelLabel(visit.channel, visit.source)}
          {visit.source && !/^\((direct|none|not set)\)$/.test(visit.source) ? ` (${visit.source})` : ''}
        </span>
      </p>
      <ul className="mt-1 space-y-0.5 border-l border-border pl-3">
        {visit.items.map((item, j) => (
          <li
            key={`${item.at}-${j}`}
            className={`text-body-sm ${item.kind === 'conversion' ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}
          >
            <span className="tabular-nums text-text-tertiary mr-2">{fmtTime(item.at)}</span>
            {describe(item)}
          </li>
        ))}
      </ul>
    </li>
  );
}
